<template>
  <modal
    :id="modalId"
    ref="modal"
    class="edit-user-profile__modal with-subtitle"
    @close="close"
    :title="$t('profile_edit_title')"
    is-simplified-modal
  >
    <template v-slot:title>
      <h2 class="title">{{ $t('profile_edit_title') }}
        <span
          class="mandatory-message"
        >{{
            $t('all_fields_with_asterisk_are_mandatory')
          }}</span>
      </h2>
    </template>
    <template v-slot:body>
      <div class="edit-user-profile__modal-content">
        <div
          class="row"
          style="margin-bottom: 0;"
        >
          <!-- Profile photo -->
          <div class="col-xs-12 tw-flex items-center gap-4 mb-4">
            <div class="relative shrink-0" style="width: 72px; height: 72px;">
              <div
                class="w-full h-full rounded-full overflow-hidden bg-gray-100 tw-flex items-center justify-center cursor-pointer border-2 border-gray-200 hover:border-primary transition-colors"
                @click="$refs.photoInput.click()"
                style="width: 72px; height: 72px;"
              >
                <img
                  v-if="photoPreview"
                  :src="photoPreview"
                  alt="Profile photo"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-2xl font-semibold text-gray-400">
                  {{ user.name ? user.name[0].toUpperCase() : '?' }}
                </span>
              </div>
              <button
                type="button"
                @click="$refs.photoInput.click()"
                class="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-primary tw-flex items-center justify-center shadow"
                style="bottom: 0; right: 0;"
              >
                <svg class="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </button>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                class="hidden"
                style="display: none;"
                @change="onPhotoSelected"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-gray-700">Profile photo</p>
              <p class="text-xs text-gray-400 mt-0.5">Click to upload a new photo</p>
            </div>
          </div>

          <form-group
            is-simlified
            :label="$t('profile_name_label')"
            required
            :error="errors.name"
            class="col-xs-12"
          >
            <ds-input
              is-simplified
              v-model="user.name"
            />
          </form-group>

          <!-- Language Tabs -->
          <div class="language-tabs w-full ml-4 mb-4 mr-4">
            <div class="tw-flex gap-2">
              <button
                v-for="lang in languages"
                :key="lang.code"
                type="button"
                @click="setActiveLanguage(lang.code)"
                :class="['language-tab', { active: activeLanguage === lang.code }]"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>

          <!-- Bio Field -->
          <form-group
            is-simplified
            :label="$t('profile_edit_short_description')"
            :error="errors.bio"
            class="col-xs-12"
          >
            <ds-textarea
              is-simplified
              v-model="profileTranslations[activeLanguage].bio"
            />
          </form-group>

          <!-- Department Field -->
          <!--            :label="$t('profile_edit_department')"-->
          <form-group
            is-simplified
            :label="departmentLabel"
            :required="!departmentFunctionFieldsOptional"
            :error="errors.department"
            v-if="departmentOptions && departmentOptions.length"
            class="col-xs-12 col-sm-6"
          >
            <dropdown
              is-simplified
              :model-value="userDepartment"
              v-model="userDepartment"
              :options="departments"
            />
          </form-group>

          <!-- Function Field -->
          <!--            :label="$t('profile_edit_function')"-->
          <form-group
            is-simplified
            :label="experienceLevelLabel"
            :required="!departmentFunctionFieldsOptional"
            :error="errors.function"
            v-if="functionOptions && functionOptions.length"
            class="col-xs-12 col-sm-6"
          >
            <dropdown
              is-simplified
              :model-value="userExperienceLevel"
              v-model="userExperienceLevel"
              :options="experienceLevels"
            />
          </form-group>

          <form-group
            is-simplified
            :label="$t('profile_edit_expertises', {expertises: getTaxonomyAliasWithDefault('expertises', false, $t('actor_edit_expertises'))})"
            required
            :error="errors.expertises"
            v-if="expertiseOptions && expertiseOptions.length"
            class="col-xs-12 col-sm-6"
          >
            <dropdown
              is-simplified
              v-model="user.expertises"
              multiple
              :options="expertiseOptions"
            />
          </form-group>
          <form-group
            is-simplified
            :label="$t('profile_edit_motivations', {motivations: getTaxonomyAlias('motivations', false, $t('actor_edit_motivations'))})"
            required
            v-if="motivationOptions && motivationOptions.length"
            :error="errors.motivations"
            class="col-xs-12 col-sm-6"
          >
            <dropdown
              is-simplified
              v-model="user.motivations"
              multiple
              :options="motivationOptions"
            />
          </form-group>

          <div class="border border-bottom border-gray-300 w-full mb-4 ml-4 mr-4"></div>

          <form-group
            is-simplified
            :label="$t('profile_edit_linkedin')"
            :error="errors.linkedin_url"
            class="col-xs-12 col-sm-6"
          >
            <ds-input
              is-simplified
              v-model="user.linkedin_url"
            />
          </form-group>
          <form-group
            is-simplified
            :label="$t('profile_edit_instagram')"
            :error="errors.instagram_url"
            class="col-xs-12 col-sm-6"
          >
            <ds-input
              is-simplified
              v-model="user.instagram_url"
            />
          </form-group>
          <form-group
            is-simplified
            :label="$t('profile_edit_twitter')"
            :error="errors.twitter_url"
            class="col-xs-12"
          >
            <ds-input
              is-simplified
              v-model="user.twitter_url"
            />
          </form-group>

          <!-- Region picker (Samergo only) -->
          <div v-if="isSamergo" class="col-xs-12 mb-4">
            <div class="tw-flex items-center justify-between mb-2">
              <label class="form-group__label__text" style="color: var(--primary); font-size: 0.85rem; font-weight: 500;">
                {{ $t('samergo_profile_region_label') }}
              </label>
              <button
                v-if="user.samergo_region"
                type="button"
                class="tw-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#ffdad6] bg-[#fff8f7] text-[#ba1a1a] hover:bg-[#ffdad6] transition-colors cursor-pointer"
                @click="user.samergo_region = ''"
              >
                <span class="material-symbols-outlined text-sm">location_off</span>
                {{ $t('samergo_profile_region_remove') }}
              </button>
            </div>
            <dropdown
              is-simplified
              v-model="user.samergo_region"
              :options="samergoRegionOptions"
              :placeholder="$t('samergo_profile_region_placeholder')"
            />
          </div>

          <!-- Location picker (map, all ecosystems) -->
          <div class="col-xs-12">
            <div class="tw-flex items-center justify-between mb-2">
              <label class="form-group__label__text" style="color: var(--primary); font-size: 0.85rem; font-weight: 500;">
                {{ $t('samergo_profile_location_label') }}
              </label>
              <div class="tw-flex gap-2">
                <button
                  type="button"
                  class="tw-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#c6c5d2] bg-white text-[#454650] hover:bg-[#f3f4f5] transition-colors cursor-pointer"
                  :disabled="locatingMe"
                  @click="locateMe"
                >
                  <span class="material-symbols-outlined text-sm">my_location</span>
                  {{ locatingMe ? $t('samergo_profile_locating') : $t('samergo_profile_locate_me') }}
                </button>
                <button
                  v-if="user.location_lat"
                  type="button"
                  class="tw-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#ffdad6] bg-[#fff8f7] text-[#ba1a1a] hover:bg-[#ffdad6] transition-colors cursor-pointer"
                  @click="clearLocation"
                >
                  <span class="material-symbols-outlined text-sm">location_off</span>
                  {{ $t('samergo_profile_location_remove') }}
                </button>
              </div>
            </div>
            <p v-if="locationError" class="text-xs text-red-500 mb-2">{{ locationError }}</p>
            <div
              ref="locationMap"
              class="rounded-xl overflow-hidden border border-[#c6c5d2]"
              style="height: 220px; background: #f0f0f0; position: relative; z-index: 0;"
            ></div>
            <p v-if="user.location_lat" class="text-xs text-slate-500 mt-1.5 tw-flex items-center gap-1">
              <span class="material-symbols-outlined text-sm text-slate-400">location_on</span>
              <span v-if="user.location_label">{{ user.location_label }}</span>
              <span class="text-slate-400">({{ user.location_lat.toFixed(4) }}, {{ user.location_lon.toFixed(4) }})</span>
            </p>
          </div>
          <!-- Preferred Language Setting -->
          <form-group
            v-if="isMember"
            is-simplified
            label="Preferred Language"
            class="col-xs-12"
            infoText="The bio of users will be visible in this language if they translated their bio in this language"
          >
            <dropdown
              is-simplified
              open-direction="top"
              v-model="user.preferred_language"
              :options="languages.map(l => ({ value: l.code, label: l.label }))"
            />
          </form-group>
        </div>

      </div>
    </template>
    <template v-slot:footer>
      <div style="width: 100%;">
        <ds-button
          variant="primary"
          @click="submit"
          :label="$t('announcement_save')"
          size="extra-small"
          :disabled="saving"
        />
      </div>
    </template>
  </modal>
</template>

<script>
  import * as L from 'leaflet'
  import 'leaflet/dist/leaflet.css'
  import Modal from './Modal.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import MODAL_IDS from '../../constants/modal-ids.js'
  import { updateProfile } from '../../api/user.js'
  import { prepareUserForEditing } from '../../util/user-settings.js'
  import DsTextarea from '../Form/DsTextarea.vue'
  import DsInput from '../Form/DsInput.vue'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import FiltersMixin from '../../util/FiltersMixin.js'
  import _isEmpty from 'lodash/isEmpty'
  import { MUTATION_TYPES as USER_MUTATION_TYPES } from '../../store/modules/user-helpers.js'
  import ConfigMixin from '../../util/ConfigMixin.js'
  import Checkbox from '@/components/Form/Checkbox.vue';
  import UserMixin from '@/util/UserMixin'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'

  const SAMERGO_REGIONS = [
    { key: 'albrandswaard', label: 'Albrandswaard', lat: 51.8557, lon: 4.4108, portfolio: 'lDnrgjNkQJ' },
    { key: 'barendrecht', label: 'Barendrecht', lat: 51.8553, lon: 4.5347, portfolio: 'JjVgNKmx2p' },
    { key: 'goeree-overflakkee', label: 'Goeree-Overflakkee', lat: 51.7453, lon: 4.0653, portfolio: '4EnmG5axQr' },
    { key: 'nissewaard', label: 'Nissewaard', lat: 51.8353, lon: 4.2697, portfolio: 'gXVp4APVQp' },
    { key: 'ridderkerk', label: 'Ridderkerk', lat: 51.8653, lon: 4.5967, portfolio: 'bGnGaejxNX' },
    { key: 'voorne-aan-zee', label: 'Voorne aan Zee', lat: 51.8753, lon: 4.0567, portfolio: 'GZx6lRjn8A' },
    { key: 'hoeksche-waard', label: 'Hoeksche-Waard', lat: 51.806599, lon: 4.348712, portfolio: 'RLn7EmEx0w' },
  ]

  export default {
    data () {
      const profile = this.$store.state.user.profile
      const user = prepareUserForEditing(profile)

      // Get supported languages from ecosystem config
      const supportedLanguages = ['en', ...(this.$store.state.config?.languages || [])]
      const defaultLanguage = supportedLanguages[0] || 'en'

      // Initialize translations for all supported languages
      // Use profile_translations if available, otherwise fall back to user fields for default language
      const profileTranslations = {}
      supportedLanguages.forEach(lang => {
        if (lang === defaultLanguage) {
          // Default language uses main fields or profile_translations
          profileTranslations[lang] = {
            bio: profile.profile_translations?.[lang]?.bio || user.bio || '',
            department: profile.profile_translations?.[lang]?.department || user.department || '',
            function: profile.profile_translations?.[lang]?.function || user.function || ''
          }
        } else {
          // Other languages only use profile_translations
          profileTranslations[lang] = {
            bio: profile.profile_translations?.[lang]?.bio || '',
            department: profile.profile_translations?.[lang]?.department || '',
            function: profile.profile_translations?.[lang]?.function || ''
          }
        }
      })
      const languageLabels = {
        en: 'English',
        nl: 'Nederlands',
        fr: 'Français',
      }
      const userLocale = this.$i18n?.locale
      const languages = supportedLanguages.map(code => ({
        code,
        label: languageLabels[code] || code.toUpperCase()
      })).sort((a, b) => {
        if (a.code === userLocale) return -1
        if (b.code === userLocale) return 1
        return 0
      })

      return {
        modalId: MODAL_IDS.EDIT_USER_PROFILE,
        errors: {},
        user: {
          ...user,
          location_lat: profile.location_lat ?? null,
          location_lon: profile.location_lon ?? null,
          location_label: profile.location_label ?? '',
          samergo_region: profile.samergo_region ?? '',
        },
        saving: false,
        photoPreview: profile.photo_url || null,
        newPhotoData: null,
        activeLanguage: supportedLanguages.includes(this.$i18n?.locale) ? this.$i18n.locale : (supportedLanguages[0] || 'en'),
        languages: languages,
        profileTranslations: profileTranslations,
        locationMap: null,
        locationMarker: null,
        locatingMe: false,
        locationError: '',
      }
    },
    computed: {
      isSamergo () {
        return this.$store.getters.isSamergo
      },
      samergoRegionOptions () {
        return SAMERGO_REGIONS.map(r => ({ value: r.key, label: r.label }))
      },
      // Departments - with translation support from store
      departments () {
        if (!this.$store.state.taxonomies.departments) {
          return []
        }
        return this.$store.state.taxonomies.departments.map((department) => {
          const translatedName = this.getTranslatedTaxonomyName(department)
          return {
            value: department.id,
            label: translatedName
          }
        })
      },
      // Experience levels - with translation support from store
      experienceLevels () {
        if (!this.$store.state.taxonomies.experienceLevels) {
          return []
        }
        return this.$store.state.taxonomies.experienceLevels.map((experienceLevel) => {
          const translatedName = this.getTranslatedTaxonomyName(experienceLevel)
          return {
            value: experienceLevel.id,
            label: translatedName
          }
        })
      },
      // Override expertiseOptions to add translations
      expertiseOptions () {
        if (!this.$store.state.taxonomies.expertises) {
          return []
        }
        return this.$store.state.taxonomies.expertises.map((expertise) => {
          const translatedName = this.getTranslatedTaxonomyName(expertise)
          return {
            value: expertise.id,
            label: translatedName
          }
        })
      },
      // Override motivationOptions to add translations
      motivationOptions () {
        if (!this.$store.state.taxonomies.motivations) {
          return []
        }
        return this.$store.state.taxonomies.motivations.map((motivation) => {
          const translatedName = this.getTranslatedTaxonomyName(motivation)
          return {
            value: motivation.id,
            label: translatedName
          }
        })
      },
      departmentFunctionFieldsOptional () {
        return !!this.$store.state.config.departmentFunctionFieldsOptional
      },
      userDepartment: {
        set (value) {
          this.user.department = value
        },
        get () {
          return Number(this.user.department)
        }
      },
      userExperienceLevel: {
        set (value) {
          this.user.function = value
        },
        get () {
          return Number(this.user.function)
        }
      }
    },
    methods: {
      // Get translated taxonomy name based on active language tab selection
      getTranslatedTaxonomyName (taxonomy) {
        if (!taxonomy) return ''

        // Use the active language from the modal tabs, not the platform-wide locale
        const currentLocale = this.activeLanguage

        // Check if translations exist for current locale
        if (taxonomy.translations && taxonomy.translations[currentLocale] && taxonomy.translations[currentLocale].name) {
          return taxonomy.translations[currentLocale].name
        }

        // Fallback to default name
        return taxonomy.name
      },
      currentUser () {
        const profile = this.$store.state.user.profile
        return prepareUserForEditing(profile)
      },
      onPhotoSelected (event) {
        const file = event.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (e) => {
          this.newPhotoData = e.target.result
          this.photoPreview = e.target.result
        }
        reader.readAsDataURL(file)
      },
      setActiveLanguage (lang) {
        this.activeLanguage = lang
      },
      validateForm () {
        this.errors = {}
        if (!this.user.name) {
          this.errors.name = this.$t('profile_error_name_required')
        }

        if (this.expertiseOptions && this.expertiseOptions.length && this.user.expertises.length === 0) {
          this.errors.expertises = this.$t('profile_error_expertise_required')
        }
        if (this.motivationOptions && this.motivationOptions.length && this.user.motivations.length === 0) {
          this.errors.motivations = this.$t('profile_error_motivation_required')
        }

        if (!this.departmentFunctionFieldsOptional) {
          if (this.departmentOptions && this.departmentOptions.length && !this.user.department) {
            this.errors.department = this.$t('profile_error_department_required')
          }
          if (this.functionOptions && this.functionOptions.length && !this.user.function) {
            this.errors.function = this.$t('profile_error_function_required')
          }
        }
      },
      async submit () {
        this.saving = true
        this.validateForm()

        if (!_isEmpty(this.errors)) {
          this.saving = false
          return
        }

        // English translations become the main user fields
        const englishTranslations = this.profileTranslations.en || {}

        const payload = {
          name: this.user.name,
          bio: englishTranslations.bio || '',
          expertises: this.user.expertises,
          motivations: this.user.motivations,
          department: this.userDepartment || '',
          function: this.userExperienceLevel || '',
          linkedin_url: this.user.linkedin_url,
          instagram_url: this.user.instagram_url,
          twitter_url: this.user.twitter_url,
          preferred_language: this.user.preferred_language,
          profile_translations: this.profileTranslations,
          location_lat: this.user.location_lat,
          location_lon: this.user.location_lon,
          location_label: this.user.location_label || null,
          samergo_region: this.user.samergo_region || null,
        }

        if (this.newPhotoData) {
          payload.photo_url = this.newPhotoData
        }

        updateProfile(payload)
          .then(data => {
            this.$store.commit(USER_MUTATION_TYPES.UPDATE_PROFILE, data)
            this.$bus.emit('updateUserProfile')
            this.$bus.emit('fetchTractionTips') // Trigger re-fetch if user is on personal home
            this.close()
            this.saving = false
          })
          .catch(errors => {
            this.errors = errors
            this.saving = false
          })
      },
      initLocationMap () {
        const el = this.$refs.locationMap
        if (!el || this.locationMap) return

        delete L.Icon.Default.prototype._getIconUrl
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        })

        const defaultCenter = [50.85, 4.35] // Belgium
        const defaultZoom = 7
        const hasLocation = this.user.location_lat && this.user.location_lon

        this.locationMap = L.map(el, { zoomControl: true, scrollWheelZoom: false }).setView(
          hasLocation ? [this.user.location_lat, this.user.location_lon] : defaultCenter,
          hasLocation ? 13 : defaultZoom
        )

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
          maxZoom: 19,
        }).addTo(this.locationMap)

        if (hasLocation) {
          this.placeMarker(this.user.location_lat, this.user.location_lon)
        }

        this.locationMap.on('click', (e) => {
          this.setLocation(e.latlng.lat, e.latlng.lng)
        })
      },
      placeMarker (lat, lon) {
        if (this.locationMarker) {
          this.locationMarker.setLatLng([lat, lon])
        } else {
          this.locationMarker = L.marker([lat, lon], { draggable: true }).addTo(this.locationMap)
          this.locationMarker.on('dragend', (e) => {
            const pos = e.target.getLatLng()
            this.setLocation(pos.lat, pos.lng)
          })
        }
      },
      async setLocation (lat, lon) {
        this.user.location_lat = lat
        this.user.location_lon = lon
        this.placeMarker(lat, lon)

        // Reverse geocode via internal GeoNames index
        try {
          const res = await fetch(`/api/geo/reverse?lat=${lat}&lon=${lon}`)
          const data = await res.json()
          this.user.location_label = data.city || ''
        } catch {
          this.user.location_label = ''
        }
      },
      async locateMe () {
        if (!navigator.geolocation) {
          this.locationError = this.$t('samergo_profile_geo_unsupported')
          return
        }
        this.locatingMe = true
        this.locationError = ''
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.locatingMe = false
            const { latitude, longitude } = pos.coords
            this.locationMap.setView([latitude, longitude], 14)
            this.setLocation(latitude, longitude)
          },
          () => {
            this.locatingMe = false
            this.locationError = this.$t('samergo_profile_geo_error')
          },
          { timeout: 10000 }
        )
      },
      clearLocation () {
        this.user.location_lat = null
        this.user.location_lon = null
        this.user.location_label = ''
        if (this.locationMarker) {
          this.locationMap.removeLayer(this.locationMarker)
          this.locationMarker = null
        }
      },
      close () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
        this.$emit('close')
      },
    },
    mixins: [FiltersMixin, ConfigMixin, UserMixin, TaxonomyMixin],
    mounted () {
      if (!this.profile.preferred_language) {
        // get browser language
        if (['nl', 'fr', 'en'].includes(window.navigator.language)) {
          this.user.preferred_language = window.navigator.language
        }
      } else {
        this.user.preferred_language = this.profile.preferred_language
      }

      this.$nextTick(() => this.initLocationMap())
    },
    beforeUnmount () {
      if (this.locationMap) {
        this.locationMap.remove()
        this.locationMap = null
      }
    },
    components: {
      Checkbox,
      Modal,
      DsTextarea,
      DsInput,
      Dropdown,
    },
  }
</script>

<style lang="scss">
  @import "../../../scss/_variables.scss";

  .edit-user-profile__modal.modal__simplified.with-subtitle {
    .modal__footer {
      width: 100%;

      .button {
        width: 100%;
        border-radius: $default-border-radius-narrow;
      }
    }

    .edit-user-profile__modal-content {
      padding: 20px;
    }

    .form-group {
      .form-group__label__text {
        color: var(--primary);
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .title-with-tabs {
      width: 100%;

      .title {
        margin-bottom: 15px;
      }

      .language-tabs {
        margin-bottom: 10px;
        display: block;

        .tw-flex {
          display: flex;
        }
      }
    }

    .language-tabs {
      border-bottom: 2px solid #e0e0e0;
      margin-bottom: 20px;

      .tw-flex {
        gap: 4px;
      }

      .language-tab {
        padding: 10px 20px;
        border: none;
        border-bottom: 3px solid transparent;
        background: transparent;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 14px;
        font-weight: 500;
        color: #666;
        margin-bottom: -2px;
        position: relative;

        &:hover {
          color: #333;
          background: rgba(0, 0, 0, 0.02);
        }

        &.active {
          color: var(--primary);
          border-bottom-color: var(--primary);
          background: transparent;
        }
      }
    }
  }

  /* Undo the global z-index: 5 override that buries controls under the tile pane */
  .leaflet-bottom,
  .leaflet-top {
    z-index: 800 !important;
  }

  /* Restore Leaflet zoom control appearance overridden by app-wide link styles */
  .leaflet-control-zoom a {
    display: flex !important;
    align-items: center;
    justify-content: center;
    width: 26px !important;
    height: 26px !important;
    background-color: #fff !important;
    color: #333 !important;
    font-size: 18px !important;
    line-height: 1 !important;
    text-decoration: none !important;
    border-bottom: 1px solid #ccc;

    &:hover {
      background-color: #f4f4f4 !important;
      color: #333 !important;
    }
  }
</style>
